import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HeroButton } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Upload, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  description: string | null;
  avatar_url: string | null;
  is_public: boolean;
  created_at: string;
}

interface EditPetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pet: Pet | null;
  onSuccess?: () => void;
}

const EditPetModal = ({ open, onOpenChange, pet, onSuccess }: EditPetModalProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    species: '',
    breed: '',
    age: '',
    gender: '',
    description: '',
    is_public: true,
  });

  const species = [
    'Cane', 'Gatto', 'Coniglio', 'Uccello', 'Pesce', 
    'Tartaruga', 'Criceto', 'Furetto', 'Altro'
  ];

  useEffect(() => {
    if (pet && open) {
      setFormData({
        name: pet.name,
        species: pet.species,
        breed: pet.breed || '',
        age: pet.age?.toString() || '',
        gender: pet.gender || '',
        description: pet.description || '',
        is_public: pet.is_public,
      });
      setAvatarPreview(pet.avatar_url);
    }
  }, [pet, open]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return null;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const fileExt = avatarFile.name.split('.').pop();
    const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('pet-avatars')
      .upload(fileName, avatarFile);

    if (uploadError) {
      console.error('Error uploading avatar:', uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from('pet-avatars')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !pet) return;

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Sessione scaduta",
          description: "Effettua nuovamente l'accesso.",
          variant: "destructive",
        });
        return;
      }

      const avatarUrl = await uploadAvatar();

      const updateData = {
        name: formData.name,
        species: formData.species,
        breed: formData.breed || null,
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        description: formData.description || null,
        is_public: formData.is_public,
        ...(avatarUrl && { avatar_url: avatarUrl }),
      };

      const { error } = await supabase
        .from('pets')
        .update(updateData)
        .eq('id', pet.id)
        .eq('user_id', session.user.id);

      if (error) throw error;

      toast({
        title: "Pet aggiornato!",
        description: `${formData.name} è stato aggiornato con successo.`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error updating pet:', error);
      toast({
        title: "Errore",
        description: "Non è stato possibile aggiornare il pet.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !pet) return;

    if (!confirm(`Sei sicuro di voler eliminare ${pet.name}? Questa azione non può essere annullata.`)) {
      return;
    }

    setDeleteLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Sessione scaduta",
          description: "Effettua nuovamente l'accesso.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('pets')
        .delete()
        .eq('id', pet.id)
        .eq('user_id', session.user.id);

      if (error) throw error;

      toast({
        title: "Pet eliminato",
        description: `${pet.name} è stato eliminato.`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error deleting pet:', error);
      toast({
        title: "Errore",
        description: "Non è stato possibile eliminare il pet.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getSpeciesEmoji = (species: string) => {
    switch (species.toLowerCase()) {
      case 'cane': return '🐕';
      case 'gatto': return '🐱';
      case 'coniglio': return '🐰';
      case 'uccello': return '🐦';
      case 'pesce': return '🐠';
      case 'tartaruga': return '🐢';
      case 'criceto': return '🐹';
      case 'furetto': return '🦫';
      default: return '🐾';
    }
  };

  if (!pet) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/20 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-center">Modifica {pet.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Upload */}
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <Avatar className="w-24 h-24 ring-4 ring-primary/30">
                <AvatarImage src={avatarPreview || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-2xl">
                  {formData.species ? getSpeciesEmoji(formData.species) : <Camera className="w-8 h-8" />}
                </AvatarFallback>
              </Avatar>
              <label 
                htmlFor="avatar-upload"
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/80 transition-colors"
              >
                <Upload className="w-4 h-4 text-white" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="glass border-white/20 text-white"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="species" className="text-white">Specie *</Label>
              <Select value={formData.species} onValueChange={(value) => setFormData({ ...formData, species: value })}>
                <SelectTrigger className="glass border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-white/20">
                  {species.map((s) => (
                    <SelectItem key={s} value={s} className="text-white">
                      {getSpeciesEmoji(s)} {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="breed" className="text-white">Razza</Label>
              <Input
                id="breed"
                value={formData.breed}
                onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                className="glass border-white/20 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="age" className="text-white">Età</Label>
              <Input
                id="age"
                type="number"
                min="0"
                max="30"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="glass border-white/20 text-white"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender" className="text-white">Sesso</Label>
            <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
              <SelectTrigger className="glass border-white/20 text-white">
                <SelectValue placeholder="Seleziona" />
              </SelectTrigger>
              <SelectContent className="glass border-white/20">
                <SelectItem value="maschio" className="text-white">♂️ Maschio</SelectItem>
                <SelectItem value="femmina" className="text-white">♀️ Femmina</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-white">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="glass border-white/20 text-white resize-none"
              rows={3}
            />
          </div>

          {/* Privacy Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_public"
              checked={formData.is_public}
              onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="is_public" className="text-white">Profilo pubblico</Label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading || deleteLoading}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={loading || deleteLoading}
              className="px-4"
            >
              {deleteLoading ? (
                "Eliminazione..."
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
            <HeroButton 
              type="submit" 
              className="flex-1" 
              disabled={!formData.name || loading || deleteLoading}
            >
              {loading ? "Salvataggio..." : "Salva"}
            </HeroButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPetModal;